import { createColumnHelper } from '@tanstack/react-table';
import { Bonus } from '@pxtra-client-lib-legacy/gen';
import { ActionsCellBonus } from './cells/actions-cell';
import Util from '../../../../misc/util';
import { format } from 'date-fns';
import { Chip } from '../../atomic';
import { Box, ChipProps, Typography } from '@mui/material';
import { PeopleIcon } from '../../../icons';
import { TFunction } from 'i18next';
import { EditCell } from './cells/edit-cell';
import { TooltipComponent } from '../../atomic/feedback';

const columnHelper = createColumnHelper<Bonus>();

export const createBonusColumns = (t: TFunction) => {
  return [
    columnHelper.accessor('title', {
      header: t('bonus:title'),
      enableColumnFilter: false
    }),
    columnHelper.accessor('tax_treatment', {
      header: t('bonus:taxTreatment'),
      cell: ({ getValue }) => t(`bonus:${getValue()}:shortName`),
      meta: {
        filterVariant: 'select',
        filterOptions: [
          { value: 'SPECIAL_OCCASION', text: t('bonus:SPECIAL_OCCASION:shortName') },
          { value: 'NON_CASH_GIFT', text: t('bonus:NON_CASH_GIFT:shortName') },
          { value: 'NON_CASH_BENEFIT', text: t('bonus:NON_CASH_BENEFIT:shortName') },
          { value: 'FULLY_TAXED', text: t('bonus:FULLY_TAXED:shortName') }
        ]
      },
      filterFn: 'equals'
    }),
    columnHelper.accessor('frequency', {
      header: t('bonus:temporalDimension'),
      cell: ({ getValue }) =>
        getValue() ? (
          <Chip
            label={t(`bonus:${getValue()}`)}
            color={getValue() === 'ONE_TIME' ? 'success' : 'heaven'}
          />
        ) : null,
      meta: {
        filterVariant: 'select',
        filterOptions: [
          { value: 'RECURRING', text: t('bonus:recurring') },
          { value: 'ONE_TIME', text: t('bonus:oneTime') }
        ]
      },
      filterFn: 'equals'
    }),
    columnHelper.accessor('upcoming_payout_month', {
      header: t('bonus:nextPayout'),
      cell: ({ row }) =>
        row.original.upcoming_payout_month
          ? format(new Date(row.original.upcoming_payout_month), 'MMMM yy', {
              locale: Util.getLocale()
            })
          : '',
      meta: {
        filterVariant: 'select',
        filterOptions: Array.from({ length: 12 }, (_, i) => ({
          value: i,
          text: format(new Date(2000, i, 1), 'MMMM', { locale: Util.getLocale() })
        }))
      },
      filterFn: (row, _columnId, filterValue) => {
        if (!row.original.upcoming_payout_month) return false;
        return new Date(row.original.upcoming_payout_month).getMonth() === filterValue;
      },
      enableGlobalFilter: true
    }),
    columnHelper.accessor('status', {
      header: t('bonus:status'),
      cell: ({ getValue }) => {
        const statusColorMap: Record<string, ChipProps['color']> = {
          ACTIVE: 'heaven',
          COMPLETED: 'success',
          CANCELED: 'error',
          PAUSED: 'warning',
          WAITING: 'warning'
        };

        const status = getValue() ?? 'WAITING';
        const color = statusColorMap[status] ?? 'heaven';
        return <Chip label={t(`bonus:${status}`)} color={color} />;
      },
      meta: {
        filterVariant: 'select',
        filterOptions: [
          { value: 'ACTIVE', text: t('bonus:ACTIVE') },
          { value: 'COMPLETED', text: t('bonus:COMPLETED') },
          { value: 'CANCELED', text: t('bonus:CANCELED') },
          { value: 'PAUSED', text: t('bonus:PAUSED') },
          { value: 'WAITING', text: t('bonus:WAITING') }
        ]
      },
      filterFn: 'equals'
    }),
    columnHelper.accessor('amount', {
      header: t('bonus:amount'),
      cell: ({ getValue }) => (
        <div className="text-right mr-3">{Util.formatCurrency(getValue())}</div>
      ),
      meta: {
        filterVariant: 'range'
      },
      filterFn: Util.currencyFilterFn
    }),
    columnHelper.display({
      id: 'employeeCount',
      header: t('bonus:employeeCount'),
      cell: ({ row }) => {
        const total = row.original.employees.length;
        const active = row.original.payable_employee_count as number;
        return (
          <TooltipComponent arrow title={total != active && t('bonus:employeeCountTooltip')}>
            <Box className="flex items-center justify-center gap-2">
              <PeopleIcon className="[&_path]:fill-none" />
              <Typography className="font-medium">
                {active === total ? total : `${active} / ${total}`}
              </Typography>
            </Box>
          </TooltipComponent>
        );
      },
      enableColumnFilter: false
    }),
    columnHelper.accessor('annual_amount', {
      header: `${t('bonus:totalAmount')} (${new Date().getFullYear()})`,
      cell: ({ getValue }) => (
        <div className="text-right mr-3">{Util.formatCurrency(getValue())}</div>
      ),
      meta: {
        filterVariant: 'range'
      },
      filterFn: Util.currencyFilterFn
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common:ACTIONS'),
      cell: (info) => (
        <div className="flex items-center">
          <EditCell info={info} />
          <ActionsCellBonus info={info} />
        </div>
      ),
      enableColumnFilter: false
    })
  ];
};
